import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AmplitudeChartComponent } from '../../../shared/components/amplitude-chart/amplitude-chart.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PubSubService } from '../../../shared/services/pubsub.service';
import { DeviceHealthService } from '../../../shared/services/device-health.service';
import { GlobalService } from '../../../shared/services/global.service';
import { Alert, AlertFilter, AlertStatusEnum, SensorCard } from '../../../shared/data/enums';
import { ToastrService } from 'ngx-toastr';
import { HighchartsChartComponent } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { interval, Subscription } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { FeatherIconComponent } from '../../../shared/components/ui/feather-icon/feather-icon.component';

type CustomPoint = Highcharts.PointOptionsObject & {
  isFlag?: boolean;
};


@Component({
  selector: 'app-device-health',
  imports: [AmplitudeChartComponent, CommonModule, HighchartsChartComponent, FeatherIconComponent],
  templateUrl: './device-health.component.html',
  styleUrl: './device-health.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DeviceHealthComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  deviceId = '';
  deviceDesc = 'The sensory device monitors vitals and movement.';
  deviceActive = true;

  deviceName = '';
  macId: string = '';
  alerts: Alert[] = [];
  /** Raw GetAllFiltered rows for amplitude chart (same response as table page 1). */
  chartAlerts: any[] = [];
  alertsLoading = false;
  hasMoreAlerts = true;
  alertTotalRecords = 0;
  showAlertsBackToTop = false;
  /** Keyset cursor for the next API page; null when the API has no more rows. */
  private alertsCursor: string | null = null;
  /** Shared with chart window (maxVisibleBars = 60) — one initial GetAllFiltered. */
  private readonly alertsPageSize = 60;
  private alertsRequestId = 0;
  /** Prevents duplicate queryParams init for the same device. */
  private initializedMacId: string | null = null;
  selectedDevice: any;
  isDeviceLoaded = false;
  selectedDate: Date = new Date();
  isAlertsFullscreen = false;
  private initialLoadPending = 0; 

  @ViewChild(AmplitudeChartComponent)
  amplitudeChart!: AmplitudeChartComponent;
  @ViewChild('alertsTable') alertsTable?: ElementRef<HTMLDivElement>;

  Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};

  private pubSubSubscription: any;
  private pollingSubscription!: Subscription;

  /** Placeholder bars until packet/API sends real dBm. */
  private readonly defaultWifiBars = 4;

  /** WiFi signal bars for header (0–5). */
  wifiSignalBars = this.defaultWifiBars;
  wifiSignalLabel = '';
  readonly signalBarIndexes = [1, 2, 3, 4, 5];

  // ───────── SENSOR CARDS ─────────
 sensorCards: SensorCard[] = [
  { icon: 'battery', label: 'Battery Remaining Power', value: 'NA', hasProgress: true, progressValue: 0 },
  { icon: 'health', label: 'Battery State of Health', value: 'NA', hasProgress: true, progressValue: 0 },
  { icon: 'wifi', label: 'WiFi Strength', value: '', hasProgress: false, progressValue: 80, signalBars: 4 },
  { icon: 'temperature', label: 'Temperature', value: 'NA', hasProgress: false, progressValue: 0 },
  { icon: 'humidity', label: 'Humidity', value: 'NA', hasProgress: false, progressValue: 0 }
];

  constructor(
    private router: ActivatedRoute,
    private pubSubService: PubSubService,
    private deviceHealth: DeviceHealthService,
    private globalService: GlobalService,
    private toastr: ToastrService,
    private route: Router,
    private spinner: NgxSpinnerService
  ) {}

  // ─────────────────────────────────────────
  // INIT
  // ─────────────────────────────────────────
  ngOnInit(): void {

    this.initChart();

    this.router.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
      let macId = params['macId'];
      let device: any = JSON.parse(localStorage.getItem('selectedDevice') || 'null');

      if (!macId && device?.macId) {
        macId = device.macId;
      }

      if (!macId || !device) {
        this.spinner.hide();
        this.toastr.warning('Please select a device first', 'No Device');
        this.route.navigate(['/device-map']);
        return;
      }

      // Same device already initialized (queryParams can emit more than once)
      if (macId === this.initializedMacId && this.isDeviceLoaded) {
        return;
      }

      this.resetDeviceState();

      this.macId = macId;
      this.initializedMacId = macId;
      this.selectedDevice = device;
      this.deviceName = device.deviceName;
      this.isDeviceLoaded = true;

      // Wait for alerts table + amplitude graph before hiding spinner
      this.beginInitialLoad();

      this.connectToDevicePubSub();
      this.fetchAlerts();
      this.getcosmosLatestData();
      this.startPolling();
    });
  }

  private beginInitialLoad(): void {
    this.initialLoadPending = 2; // alerts + amplitude chart
    this.spinner.show();

    // Safety: never leave spinner stuck if a request hangs
    setTimeout(() => {
      if (this.initialLoadPending > 0) {
        this.initialLoadPending = 0;
        this.spinner.hide();
      }
    }, 15000);
  }

  private markInitialLoadStepDone(): void {
    if (this.initialLoadPending <= 0) {
      return;
    }

    this.initialLoadPending--;

    if (this.initialLoadPending <= 0) {
      this.spinner.hide();
    }
  }

  onChartReady(): void {
    this.markInitialLoadStepDone();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.closeAlertsFullscreen();
    this.spinner.hide();

    if (this.pubSubSubscription) {
      this.pubSubSubscription.unsubscribe();
      this.pubSubSubscription = null;
    }

    this.pubSubService.disconnect();
  }
   
  startPolling() {
    this.stopPolling();

    this.pollingSubscription = interval(6000).subscribe(() => {
      this.getcosmosLatestData();
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }


  // ─────────────────────────────────────────
  // CHART INIT
  // ─────────────────────────────────────────
  initChart() {
    // Highcharts 12: useUTC removed — set browser IANA zone (e.g. Asia/Karachi)
    const localTimezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;

    Highcharts.setOptions({
      time: { timezone: localTimezone }
    });

    this.chartOptions = {
      chart: {
        type: 'column',
        backgroundColor: 'transparent'
      },

      time: {
        timezone: localTimezone
      },

      title: { text: '' },

      xAxis: {
        type: 'datetime',
        tickInterval: 3 * 3600 * 1000,
        labels: {
          formatter: function () {
            const chart = (this as any).axis?.chart;
            if (chart?.time?.dateFormat) {
              return chart.time.dateFormat('%d %b %H:%M', this.value as number);
            }
            return Highcharts.dateFormat('%d %b %H:%M', this.value as number);
          },
          style: { color: '#888', fontSize: '10px' }
        }
      },

      yAxis: { visible: false },
      legend: { enabled: false },

      tooltip: {
        useHTML: true,
        formatter: function (this: any) {
          const chart = this.series?.chart;
          const dateTime = chart?.time?.dateFormat
            ? chart.time.dateFormat('%d %b %H:%M', this.x)
            : Highcharts.dateFormat('%d %b %H:%M', this.x);

          if (this.point?.isFlag) {
            return `
              ⏱ ${dateTime}<br/>
              🚩 <b>Flag Inference</b><br/>
            `;
          }

          return `
            ⏱ ${dateTime}<br/>
            No alerts
          `;
        }
      },

      plotOptions: {
        column: {
          pointWidth: 3,
          borderRadius: 2
        }
      },

      series: [{
        type: 'column',
        data: []
      }],

      credits: { enabled: false }
    };
  }

  // ─────────────────────────────────────────
  // DATE LABELS
  // ─────────────────────────────────────────
  generateDateLabels(): string[] {
    const labels: string[] = [];

    const today = new Date();
    const start = new Date();
    start.setDate(today.getDate() - 6); // last 7 days (including today)

    let current = new Date(start);

    while (current <= today) {
      labels.push(
        current.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short'
        })
      );

      current.setDate(current.getDate() + 1);
    }

    return labels;
  }

  // ─────────────────────────────────────────
  // BUILD CHART DATA (MAIN LOGIC)
  // ─────────────────────────────────────────


  getData(): CustomPoint[] {

    const data: CustomPoint[] = [];

    const end = new Date(this.selectedDate);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);

    // normalize full days
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    let current = new Date(start);

    while (current <= end) {

      const next = new Date(current);
      next.setHours(current.getHours() + 3);

      // check if ANY alert exists in bucket — prefer API `date` over timeStamp
      const hasAlert = this.alerts.some(a => {
        const alertDate =
          this.globalService.parseAlertDate(a.date) ??
          this.globalService.parseAlertDate(a.timeStamp) ??
          this.globalService.parseAlertDate(a.acknowledgeOn);

        if (!alertDate) return false;

        return alertDate >= current && alertDate < next;
      });

      // FIXED HEIGHT LOGIC
      const value = hasAlert ? 80 : 25; //same height for all alert bars

      data.push({
        x: current.getTime(), // correct timestamp
        y: value,
        color: hasAlert ? '#ef4444' : '#3ef37d',
        isFlag: hasAlert
      });

      current = next;
    }

    return data;
  }


  updateChart() {
    this.chartOptions = {
      ...this.chartOptions,
      series: [{
        type: 'column',
        data: this.getData()
      }]
    };
  }



  onDateChange(event: any) {
    const value = event.target.value;

    const parsedDate = value ? new Date(value) : new Date();

    if (isNaN(parsedDate.getTime())) {
      this.selectedDate = new Date();
    } else {
      this.selectedDate = parsedDate;
    }

    this.updateChart();
  }


  // ─────────────────────────────────────────
  // ALERT API
  // ─────────────────────────────────────────
  private mapApiAlert(item: any): Alert {
    return {
      id: item.id,
      type: item.type,
      macId: item.macId,
      status: item.status,
      acknowledgeOn: item.acknowledgeOn,
      resolvedOn: item.resolvedOn,
      floorName: item.floorName,
      date: item.date,
      timeStamp: item.timeStamp
    };
  }

  onAlertsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el) return;

    this.showAlertsBackToTop = el.scrollTop > 180;

    if (
      this.alertsLoading ||
      !this.hasMoreAlerts ||
      !this.macId ||
      !this.alertsCursor
    ) {
      return;
    }

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) {
      this.fetchAlerts(true);
    }
  }

  scrollAlertsToTop(): void {
    this.alertsTable?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private loadMoreIfAlertsNotScrollable(): void {
    const el = this.alertsTable?.nativeElement;
    if (
      !el ||
      this.alertsLoading ||
      !this.hasMoreAlerts ||
      !this.macId ||
      !this.alertsCursor
    ) {
      return;
    }

    if (el.scrollHeight <= el.clientHeight + 8) {
      this.fetchAlerts(true);
    }
  }

  fetchAlerts(append = false) {

    if (!this.macId) return;
    // Later pages continue from the server's cursor; without one there is nothing more to load
    if (append && (this.alertsLoading || !this.hasMoreAlerts || !this.alertsCursor)) {
      return;
    }

    if (!append) {
      this.alertsCursor = null;
      this.hasMoreAlerts = true;
      this.alerts = [];
    }

    const requestId = ++this.alertsRequestId;
    this.alertsLoading = true;

    // Pure cursor paging: first page without a cursor (newest alerts + total), every later page
    // with the keyset cursor, so alerts inserted meanwhile (live via PubSub) can't repeat rows.
    const filters: AlertFilter = append
      ? { macId: this.macId, pageSize: this.alertsPageSize, excludeResolved: true, cursor: this.alertsCursor! }
      : { macId: this.macId, pageSize: this.alertsPageSize, excludeResolved: true };

    this.deviceHealth.getAlerts(filters).subscribe({
      next: (res: any) => {
        if (requestId !== this.alertsRequestId) return;

        const { rows, totalRecords, isPaged, nextCursor } = this.globalService.extractAlertPage(res);
        const mapped = rows
          .filter((x: any) => x.status !== 'Resolved')
          .filter((x: any) => this.isListableAlertType(x.type))
          .map((item: any) => this.mapApiAlert(item));

        if (append) {
          const existingKeys = new Set(this.alerts.map(a => this.alertKey(a.id)));
          this.alerts = [
            ...this.alerts,
            ...mapped.filter(a => !existingKeys.has(this.alertKey(a.id)))
          ];
        } else {
          // Page 1 = latest alertsPageSize rows — same payload feeds the amplitude chart.
          this.chartAlerts = Array.isArray(rows) ? [...rows] : [];

          const incomingKeys = new Set(mapped.map(a => this.alertKey(a.id)));
          const liveOnly = this.alerts.filter(
            a => !incomingKeys.has(this.alertKey(a.id)) && this.isListableAlertType(a.type)
          );
          this.alerts = [...liveOnly, ...mapped];
        }

        this.sortAlertsNewestFirst();

        // The total comes with the first page only (cursor pages skip the COUNT)
        if (!append) {
          this.alertTotalRecords = isPaged
            ? Math.max(totalRecords, this.alertTotalRecords, this.alerts.length)
            : this.alerts.length;
        } else if (this.alertTotalRecords < this.alerts.length) {
          this.alertTotalRecords = this.alerts.length;
        }

        // Unpaged API returns the full list; otherwise continue while the server hands a cursor
        this.alertsCursor = nextCursor;
        this.hasMoreAlerts = isPaged && !!nextCursor;
        this.alertsLoading = false;

        this.updateChart();

        if (!append) {
          this.markInitialLoadStepDone();
        }

        // Disabled: auto page-2+ when table isn't scrollable after page 1.
        // Next pages load only on user scroll (onAlertsScroll).
        // if (this.hasMoreAlerts) {
        //   setTimeout(() => this.loadMoreIfAlertsNotScrollable());
        // }
      },
      error: err => {
        if (requestId !== this.alertsRequestId) return;
        console.error(err);
        this.alertsLoading = false;
        // Cursor is unchanged, so the next scroll simply retries the same page
        if (!append) {
          // New reference so chart rebuilds/emits chartReady even on failure
          this.chartAlerts = [];
          this.markInitialLoadStepDone();
        }
      }
    });
  }

  // Arrow property: *ngFor calls trackBy with `this` = the NgForOf directive, not this component
  trackByAlertId = (_index: number, alert: Alert): string => this.alertKey(alert.id);

  /** Normalised alert id: PubSub and the API may format the same GUID differently. */
  private alertKey(id: unknown): string {
    return String(id ?? '').trim().toLowerCase();
  }

  /** API `date` (UTC) → Date for local display via Angular date pipe. */
  getAlertDate(alert: Alert): Date | null {
    return this.globalService.getAlertDisplayDate(alert);
  }

  // ─────────────────────────────────────────
  // PUBSUB (LIVE DATA)
  // ─────────────────────────────────────────
  private connectToDevicePubSub() {

    if (!this.macId) return;
     //CLEAN OLD SUBSCRIPTION FIRST
    if (this.pubSubSubscription) {
      this.pubSubSubscription.unsubscribe();
      this.pubSubSubscription = null;
    }
  
    this.pubSubService.disconnect(this.macId, 'device');
    this.pubSubService.connect(this.macId, 'device');

    this.pubSubSubscription = this.pubSubService.messages$.subscribe((msg: any) => {
      if (!msg || msg.sourceType !== 'device' || msg.sourceId !== this.macId) {
        return;
      }

      console.log('📡 Device PubSub (macId) message:', this.macId, msg?.data ?? msg);

      const data = msg?.data;
      if (!data) return;

      this.handleIncomingDeviceData(data);
    });
  }

  handleIncomingDeviceData(data: any) {

    if (!data) return;

    if (data.MacId && data.MacId !== this.macId) {
      return;
    }

    this.upsertLiveUnresolvedAlert(data);

    const createdOnRaw = data.CreatedOn ?? data.createdOn;
    const createdOnMs =
      this.globalService.parseAlertDate(createdOnRaw)?.getTime() ??
      Date.now();

    const newPoint = {
      amplitude: Number(data.Amplitude ?? 0),
      confidence: Number(data.Confidence ?? 0),
      type: data.Type ?? 'Unknown',
      // Live PubSub: prefer CreatedOn (not TimeStamp) for chart time
      timestamp: createdOnMs,
      timeString: '',
      timeOnlyString: '',
      id: data.Id ?? data.id ?? undefined
    };

    if (this.amplitudeChart) {
      this.amplitudeChart.deviceInfo = this.getActiveDeviceInfoWithMac();
    }

    this.pushToChart(newPoint);
  }

  private upsertLiveUnresolvedAlert(data: any): void {
    const id = data.Id ?? data.id;
    if (!id) return;

    const type = data.Type ?? data.type;
    // Ambient Air must not appear in the unresolved alerts list (API + live).
    if (!this.isListableAlertType(type)) return;

    const status = this.globalService.mapAlertStatus(data.Status ?? data.status);

    if (status === 'Resolved') {
      const key = this.alertKey(id);
      const existed = this.alerts.some(a => this.alertKey(a.id) === key);
      this.alerts = this.alerts.filter(a => this.alertKey(a.id) !== key);
      if (existed && this.alertTotalRecords > 0) {
        this.alertTotalRecords--;
      }
      this.updateChart();
      return;
    }

    const createdOn = data.CreatedOn ?? data.createdOn ?? data.Date ?? data.date;
    const liveAlert: Alert = {
      id,
      type,
      macId: data.MacId ?? data.macId ?? this.macId,
      status,
      acknowledgeOn: data.AcknowledgeOn ?? data.acknowledgeOn ?? null,
      resolvedOn: data.ResolvedOn ?? data.resolvedOn ?? null,
      floorName: data.FloorName ?? data.floorName ?? this.selectedDevice?.floorName ?? '',
      date: createdOn ?? null,
      timeStamp: createdOn ?? data.TimeStamp ?? data.timeStamp ?? null
    };

    const liveKey = this.alertKey(liveAlert.id);
    const existingIndex = this.alerts.findIndex(a => this.alertKey(a.id) === liveKey);

    if (existingIndex >= 0) {
      const next = [...this.alerts];
      next.splice(existingIndex, 1);
      this.alerts = [liveAlert, ...next];
    } else {
      this.alerts = [liveAlert, ...this.alerts];
      this.alertTotalRecords++;
    }

    this.updateChart();
  }

  /** Unresolved list excludes Ambient Air and empty-type packets (API + PubSub). Chart still shows them. */
  private isListableAlertType(type: string | null | undefined): boolean {
    return this.globalService.isListableAlertType(type);
  }

  private sortAlertsNewestFirst(): void {
    this.alerts = [...this.alerts].sort((a, b) => {
      const bTime = this.getAlertDate(b)?.getTime() ?? 0;
      const aTime = this.getAlertDate(a)?.getTime() ?? 0;
      return bTime - aTime;
    });
  }

  //  Push new live data into chart
  pushToChart(data: any) {

  // Ensure chart is ready + avoid timing issues
  setTimeout(() => {
      if (!this.amplitudeChart) {
        console.warn('Chart not ready');
        return;
      }

      this.amplitudeChart.addDataPoint(data, this.macId);

    }, 0);
  }

  getActiveDeviceInfoWithMac() {
  return {
    macId: this.macId,
    floor: this.selectedDevice?.floorName ?? 'Unknown Floor',
    room: this.selectedDevice?.roomName ?? 'Unknown Room',
    deviceId: this.selectedDevice?.deviceName ?? 'Unknown Device'
  };
}

  /** Open alerts table in fullscreen overlay */
  viewAllAlerts(event?: Event): void {
    event?.preventDefault();
    this.isAlertsFullscreen = true;
    document.body.style.overflow = 'hidden';
  }

  closeAlertsFullscreen(): void {
    this.isAlertsFullscreen = false;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseAlerts(): void {
    if (this.isAlertsFullscreen) {
      this.closeAlertsFullscreen();
    }
  }
  // ─────────────────────────────────────────
  // ALERT ACTIONS
  // ─────────────────────────────────────────
  acknowledgeAlert(alert: Alert): void {

    this.deviceHealth
      .UpdateAlertStatus(alert.id, AlertStatusEnum.Acknowledged)
      .subscribe(() => {
        alert.status = 'Acknowledged';
        alert.acknowledgeOn = new Date().toISOString();

        this.toastr.info('Alert acknowledged');
        this.updateChart();
      });
  }

  resolveAlert(alert: Alert): void {

    this.deviceHealth
      .UpdateAlertStatus(alert.id, AlertStatusEnum.Resolved)
      .subscribe(() => {

        this.alerts = this.alerts.filter(a => this.alertKey(a.id) !== this.alertKey(alert.id));
        if (this.alertTotalRecords > 0) {
          this.alertTotalRecords--;
        }
        this.toastr.success('Resolved');
        this.updateChart();
      });
  }
  device: any = null;
  private cosmosRequest: any = null;

  getcosmosLatestData(): void {

  const now = new Date().toISOString();

  // Keep the request state between polling calls
  if (!this.cosmosRequest) {
    this.cosmosRequest = {
      SelectedDeviceId: this.macId,
      StartTime: now,
      EndTime: null,
      IsRealTime: true,
      Resume: false,
      orgId: this.selectedDevice?.orgId,
      received_time_stamp: null
    };
  }

  // Always make sure the selected device is current
  this.cosmosRequest.SelectedDeviceId = this.macId;

    this.deviceHealth.getCosmosLatestData(this.cosmosRequest).subscribe({
      next: (res: any) => {
        if (!res?.devices?.length) {
          return;
        }
        const device = res.devices[0];

        // Store latest device data
        this.device = device;
        // Get received timestamp FROM Cosmos response.
        // Do not generate this timestamp on frontend.
        if (device?.receivedTimeStamp) {

          this.cosmosRequest.received_time_stamp =
            device.receivedTimeStamp;

          // From the next request onward, use Resume
          this.cosmosRequest.Resume = true;
        }

        // Map device values to sensor cards
        this.sensorCards = [
          ...this.mapDeviceToSensorCards(device)
        ];
      },

      error: (err) => {
        // Don't break polling if one request fails
        console.error('Cosmos API error:', err);
      }
    });
  }


  mapDeviceToSensorCards(device: any): SensorCard[] {
    const wifi = this.globalService.resolveWifiSignal(device, this.defaultWifiBars);
    this.wifiSignalBars = wifi.bars;
    this.wifiSignalLabel = wifi.label;

    return [
      {
        icon: 'battery',
        label: 'Battery Remaining Power',
        value: 'NA',
        hasProgress: true,
        progressValue: 0
      },
      {
        icon: 'health',
        label: 'Battery State of Health',
        value: 'NA',
        hasProgress: true,
        progressValue: 0
      },
      {
        icon: 'wifi',
        label: 'WiFi Strength',
        value: wifi.label,
        hasProgress: false,
        progressValue: wifi.bars * 20,
        signalBars: wifi.bars
      },
      {
        icon: 'temperature',
        label: 'Temperature',
        value: device?.tempDegC != null ? `${device.tempDegC.toFixed(1)} °C` : 'N/A',
        hasProgress: false,
        progressValue: 0
      },
      {
        icon: 'humidity',
        label: 'Humidity',
        value: device?.rh != null ? `${Math.round(device.rh)} %` : 'N/A',
        hasProgress: false,
        progressValue: 0
      }
    ];
  }

  resetDeviceState() {

  //stop polling
  this.stopPolling();

  //unsubscribe pubsub
  if (this.pubSubSubscription) {
    this.pubSubSubscription.unsubscribe();
    this.pubSubSubscription = null;
  }

  //disconnect ONLY this device
  if (this.macId) {
    this.pubSubService.disconnect(this.macId, 'device');
  }
  // reset UI
  this.device = null;
  this.alerts = [];
  this.chartAlerts = [];
  this.alertsLoading = false;
  this.hasMoreAlerts = true;
  this.alertTotalRecords = 0;
  this.alertsCursor = null;
  this.alertsRequestId++;
  this.showAlertsBackToTop = false;
  this.wifiSignalBars = this.defaultWifiBars;
  this.wifiSignalLabel = '';
  this.isDeviceLoaded = false;
}


}